/**
 * S2 shell + S3 landing area — the state machines (seam S2).
 *
 * Pure functions over the `/v1/me` result, for the same reason the door's are:
 * the decision is testable without a DOM, and it cannot drift from what the API
 * actually returns.
 *
 * The distinction the whole file exists to protect: **"you have no areas" and
 * "we can't read your areas" are different states.** The first is a fact about
 * the person; the second is a fault in the control plane. Collapsing them shows
 * someone an empty rail during an outage and tells them, falsely, that they
 * have no access.
 */

import { API_BASE } from "./api";
import type { MeResponse } from "./api";

/**
 * UX v0.3 §5, S2 — the WHOLE-SHELL states.
 *
 * `partial` is deliberately absent from this union. The S2 partial state is
 * "areas resolved, ONE area's module list still loading", which is a property
 * of an area, not of the shell: the rest of the rail stays usable throughout.
 * It is carried per-area by `modules_status` and rendered by `AreaRail`.
 *
 * Listing it here would declare a state `resolveShellState` can never return —
 * a type that lies about its own inhabitants, and a state no test could
 * genuinely reach.
 */
export type ShellState = "loading" | "ideal" | "empty" | "error" | "single-area";

/** UX v0.3 §5, S3. Three states. */
export type LandingState = "ideal" | "empty-area" | "no-landing-configured";

export interface MeFailure {
  status: number;
  reason: string;
  message: string;
}

export type MeProbe =
  | { kind: "me"; me: MeResponse }
  | { kind: "failed"; failure: MeFailure };

/**
 * Resolve the shell state.
 *
 * `pending` is passed in rather than inferred so the caller's query library
 * owns "is it still loading", and this function owns everything else.
 */
export function resolveShellState(probe: MeProbe | undefined, pending: boolean): ShellState {
  if (pending || !probe) return "loading";
  if (probe.kind === "failed") return "error";

  const areas = probe.me.areas;
  if (areas.length === 0) return "empty";
  if (areas.length === 1) return "single-area";
  return "ideal";
}

/**
 * Resolve the landing-area state for the area the shell is about to open.
 *
 * `no-landing-configured` is **not** an error and must never render as one:
 * a config gap must not read as a fault (UX S3 edge). It exists only so the
 * caller can tell "we derived this by rail-order fallback" from "the owner
 * chose this", which matters for the access-admin screen later, not for copy.
 */
export function resolveLandingState(
  me: MeResponse,
  openAreaKey: string | null,
): LandingState {
  if (!me.landing_area) return "no-landing-configured";
  const area = me.areas.find((a) => a.area_key === openAreaKey);
  if (!area || area.modules.length === 0) return "empty-area";
  return "ideal";
}

/**
 * Which area the shell opens on: the deep-linked one if the person has it,
 * otherwise the landing area the API derived (decision D-3).
 *
 * A deep link to an area the person is not entitled to resolves to their
 * landing area, not to an error page — but the caller is told, so it can show
 * the "You don't have access to {Area}" line rather than silently redirecting.
 */
export function resolveOpenArea(
  me: MeResponse,
  requestedAreaKey: string | null,
): { areaKey: string | null; deniedRequest: boolean } {
  // No landing area configured falls back to the first entitled area by rail
  // order, shown with NO error: a config gap must not read as a fault (UX S3 edge).
  const fallback = me.landing_area ?? me.areas[0]?.area_key ?? null;

  if (requestedAreaKey) {
    const entitled = me.areas.some((a) => a.area_key === requestedAreaKey);
    if (entitled) return { areaKey: requestedAreaKey, deniedRequest: false };
    return { areaKey: fallback, deniedRequest: true };
  }
  return { areaKey: fallback, deniedRequest: false };
}

/**
 * A display name for a recorded owner, from their Ketosource address.
 *
 * This transforms data we HAVE; it never invents an owner. A null
 * `owner_email` stays null and the badge names Damien instead — the one thing
 * this must not do is turn "nobody is recorded" into a name.
 */
export function ownerDisplayName(email: string | null): string | null {
  if (!email) return null;
  const local = email.split("@")[0] ?? "";
  const first = local.split(/[._-]/)[0] ?? "";
  return first ? first.charAt(0).toUpperCase() + first.slice(1) : null;
}

/**
 * Fetch the nav tree. Never throws — a failure is a state, not an exception.
 *
 * `reload` skips the browser's copy and overwrites it. `/v1/me` is served
 * `private, max-age=60`, and the person it describes changes when a view-as
 * opens or closes without the URL changing, so a plain re-read can return the
 * previous person's nav for up to a minute (bug 3f80402f).
 */
export async function probeMe(
  signal?: AbortSignal,
  fresh = false,
  reload = false,
): Promise<MeProbe> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/v1/me${fresh ? "?fresh=1" : ""}`, {
      credentials: "include",
      signal,
      ...(reload ? { cache: "reload" as RequestCache } : {}),
    });
  } catch {
    return {
      kind: "failed",
      failure: {
        status: 0,
        reason: "unreachable",
        message: "We can't confirm your access right now.",
      },
    };
  }

  if (res.ok) return { kind: "me", me: (await res.json()) as MeResponse };

  let reason = "unreachable";
  let message = "We can't confirm your access right now.";
  try {
    const body = (await res.json()) as { error?: string; message?: string };
    if (body.error) reason = body.error;
    if (body.message) message = body.message;
  } catch {
    // No JSON body (an edge error page, say) — keep the defaults.
  }
  return { kind: "failed", failure: { status: res.status, reason, message } };
}

/**
 * Refusals that mean "this person's sign-in lapsed", not "the control plane is
 * down". The same three codes the S1 door already sends to sign in
 * (`resolveDoorState` in session.ts). The shell used to show them as a fault
 * with a Try again button that re-ran the same failing probe, so a person whose
 * Access session expired with the portal open was stranded.
 */
const SIGN_IN_REASONS = new Set(["no_identity", "expired_identity", "invalid_identity"]);

export function needsSignIn(failure: MeFailure | null | undefined): boolean {
  return !!failure && SIGN_IN_REASONS.has(failure.reason);
}

/** sessionStorage key for the last automatic sign-in redirect (ms epoch). */
export const SIGN_IN_ATTEMPT_KEY = "ks.portal.signInAttemptAt";

/**
 * A second automatic redirect inside this window means signing in did not fix
 * the probe (say the edge accepted the session but the API still sees none).
 * Redirecting again would loop forever, so the shell stops and offers a button.
 */
export const SIGN_IN_RETRY_WINDOW_MS = 2 * 60 * 1000;

type AttemptStore = Pick<Storage, "getItem" | "setItem">;

/**
 * Send the browser through sign-in and back to the page it was on — once.
 *
 * Signing in is a navigation, not an API call (see `SHELL_PATH` in session.ts):
 * every `/app/...` path is behind the Cloudflare Access application, so
 * reloading the current URL makes Access run the Google sign-in and return to
 * exactly this URL (`redirect_url` is the current path). Returns `false`, and
 * navigates nowhere, when an automatic redirect already happened inside
 * `SIGN_IN_RETRY_WINDOW_MS`; the caller then shows a button instead.
 */
export function redirectToSignIn(
  reload: () => void,
  store: AttemptStore | null,
  now: number = Date.now(),
  force = false,
): boolean {
  let last = 0;
  try {
    last = Number(store?.getItem(SIGN_IN_ATTEMPT_KEY) ?? 0) || 0;
  } catch {
    // Storage refused (private mode, blocked site data): no loop guard, so
    // never redirect automatically — the button still works.
    if (!force) return false;
  }
  if (!force && (!store || now - last < SIGN_IN_RETRY_WINDOW_MS)) return false;
  try {
    store?.setItem(SIGN_IN_ATTEMPT_KEY, String(now));
  } catch {
    // Best effort: a failed write only weakens the guard for a forced click.
  }
  reload();
  return true;
}
